import { useLoadingPromise } from "@/hooks/useLoadingPromise";
import {
  getCarAPI,
  getUploadedSettingFilesAPI,
  uploadSettingFileAPI,
} from "@/util/api/vehicle";
import { useStatefulNavigator } from "@/util/useStatefulNavigator";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Axios from "axios";
import toast from "@/components/hoc/SnackbarAlert";
import { VehicleConstants } from "./VehicleConstants";
import { Messages } from "@/util/Messages";
import {
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import Title from "@/components/molecules/Title";
import { TitleGroup } from "@/components/molecules/TitleGroup";
import { Car } from "@/models/vehicle";
import TextField from "@/components/atoms/TextField";
import { Button } from "@/components/molecules/Button";
import ClearIcon from "@mui/icons-material/Clear";
import { CommonButton } from "@/components/common/buttons/CommonButton";
import { ActionButtons } from "@/components/common/buttonGroup/ActionButtons";

export const SettingFileUpload = () => {
  const params = useParams<{ id: string }>();
  const id = params.id != null ? Number(params.id) : undefined;

  const { navigate } = useStatefulNavigator();

  const car = useCar(id);
  const [settingFiles, refetchSettingFiles] = useSettingFiles(car?.evCars?.id);

  const [file, setFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [hasError, setHasError] = useState(false);
  const [loading, uploadApi] = useLoadingPromise(uploadSettingFileAPI);

  const goToDetail = () => navigate(`/cars/${id}`);

  const handleSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasError(false);

    if (e.target.files == null || e.target.files.length === 0) {
      return;
    }

    setFile(e.target.files[0]);

    if (inputRef.current != null) {
      inputRef.current.value = "";
    }
  };

  const clearFile = () => {
    setFile(null);
    if (inputRef.current != null) {
      inputRef.current.value = "";
    }
  };

  const upload = async () => {
    if (car?.evCars?.id == null || loading) {
      return;
    }

    if (file == null) {
      setHasError(true);
      return;
    }

    try {
      await uploadApi(car.evCars.id, file);
      clearFile();
      toast.success(`${VehicleConstants.SettingFileUpload}に成功しました。`);
    } catch (e) {
      if (
        Axios.isAxiosError(e) &&
        [404, 409].includes(e.response?.status ?? -1)
      ) {
        toast.error(
          `${VehicleConstants.SettingFileUpload}${Messages.ErrorOccurred}: ${
            e.response?.data?.message ?? ""
          }`
        );
      }
      console.error("===Error occurred while uploading setting file===", e);
    } finally {
      refetchSettingFiles();
    }
  };

  useEffect(() => {
    if (id == null) {
      return;
    }

    if (car != null && car.evCars?.id == null) {
      console.error(
        "EV車両が登録されていないため、設定ファイルをアップロードできません。"
      );
      navigate(`/cars/${id}`);
    }
  }, [car, navigate, id]);

  return (
    <>
      <Grid container spacing={2} sx={{ px: 2, mt: 3 }}>
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title style={{ flexGrow: 1 }}>
            {VehicleConstants.SettingFileUpload}
          </Title>
          <CommonButton
            btnType="Return"
            sx={{
              height: 35,
            }}
            onClickBtn={goToDetail}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ px: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <Table
                size="small"
                className="gx-info-table-no-width"
                sx={{ width: "70%" }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell className="body-none-custom">
                      <TitleGroup
                        style={{
                          marginBottom: 0,
                          marginTop: 20,
                        }}
                        text={VehicleConstants.BasicInfomation}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={VehicleConstants.CompanyOffice}
                        value={car?.office?.displayName ?? ""}
                        multiline
                        InputProps={{
                          readOnly: true,
                          disableUnderline: true,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={VehicleConstants.RadioNumber}
                        value={car?.vehicleCode ?? ""}
                        multiline
                        InputProps={{
                          readOnly: true,
                          disableUnderline: true,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={VehicleConstants.IMEI}
                        value={car?.evCars?.imei ?? ""}
                        multiline
                        InputProps={{
                          readOnly: true,
                          disableUnderline: true,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Table size="small" sx={{ width: "70%" }}>
                <TableHead>
                  <TableRow>
                    <TableCell className="body-none-custom">
                      <TitleGroup
                        style={{
                          marginBottom: 0,
                          marginTop: 20,
                        }}
                        text={VehicleConstants.SettingFile}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell
                      className="required"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <TextField
                        label={VehicleConstants.SettingFile}
                        value={file?.name ?? ""}
                        multiline
                        InputProps={{
                          readOnly: true,
                          disableUnderline: true,
                        }}
                        sx={{ width: "100%" }}
                        error={hasError}
                        errorLabel={`${VehicleConstants.SettingFile}を選択してください`}
                      />
                      {file != null && (
                        <IconButton
                          size="small"
                          sx={{ width: "48px" }}
                          onClick={clearFile}
                        >
                          <ClearIcon />
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell
                      align="right"
                      className="body-none-custom"
                      sx={{ width: "18.5%" }}
                    >
                      <Button
                        label={VehicleConstants.SelectSettingFile}
                        onClick={() => inputRef.current?.click()}
                        disabled={loading}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Table size="small" sx={{ width: "70%", mt: 2 }}>
                <TableBody>
                  {settingFiles.map((fileName) => (
                    <TableRow key={fileName}>
                      <TableCell
                        sx={{
                          border: "1px solid #e0e0e0",
                          wordBreak: "break-all",
                        }}
                      >
                        {fileName}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <ActionButtons
                loading={loading}
                onCancel={goToDetail}
                onSubmit={upload}
              />
            </Paper>
          </Grid>
        </Grid>
      </Grid>
      <input
        type="file"
        style={{ display: "none" }}
        ref={inputRef}
        onChange={handleSelectFile}
      />
    </>
  );
};

const useCar = (id?: number) => {
  const [car, setCar] = useState<Car | null>(null);

  useEffect(() => {
    if (id == null) {
      return;
    }

    const load = async () => {
      try {
        const carRes = await getCarAPI(id);
        setCar(carRes.data);
      } catch (e) {
        console.error("===Error occurred while loading setting files===", e);
      }
    };

    load();
  }, [id]);

  return car;
};

type UseSettingFilesReturn = [string[], () => Promise<void>];
const useSettingFiles = (evCarId?: number): UseSettingFilesReturn => {
  const [settingFiles, setSettingFiles] = useState<string[]>([]);

  const load = useCallback(async () => {
    if (evCarId == null) {
      return;
    }

    try {
      const res = await getUploadedSettingFilesAPI(evCarId);
      setSettingFiles(res.data);
    } catch (e) {
      console.error("===Error occurred while loading setting files===", e);
    }
  }, [evCarId]);

  useEffect(() => {
    load();
  }, [load]);

  return [settingFiles, load];
};
